<template>
    <div class="col resubmit-container justify-content-center">
        <div v-bind:class="['inputs-wrapper', 'territory-' + territory]">
            <div class="input-container">
                <div class="label">{{ translations.loanAmount.label }}</div>
                <MoneyField
                    name="loanAmount"
                    ref="loanAmountComponent"
                    :label="$t('form.fields.loanAmount.label')"
                    :validationRules="{
                        amountBetween: {
                            min: minLoanAmount,
                            max: maxLoanAmount,
                            errorMessage: $t('form.fields.loanAmount.amountBetweenErrorMessage'),
                        },
                    }"
                    :maxlength="9"
                    :min="minLoanAmount"
                    :max="maxLoanAmount"
                    vuexKey="data"
                />
            </div>

            <div class="input-container" v-if="territory !== 'usa'">
                <div class="label">{{ translations.loanTerm.label }}</div>
                <SelectField
                    name="loanTerm"
                    ref="loanTermComponent"
                    :label="$t('results.resubmit.fields.loanTerm.label')"
                    icon="far fa-calendar-alt"
                    class="desktop-only"
                    :options="loanTerms"
                    :validationRules="{
                        required: true,
                    }"
                    vuexKey="data"
                />
                <ButtonSelectField
                    class="mobile-only"
                    :options="loanTerms"
                    name="loanTerm"
                    :customErrorMessage="translations.loanTerm.errorMessage"
                    :validationRules="{
                        required: true,
                    }"
                    buttonClasses="bordered filter-option-button"
                    vuexKey="data"
                />
            </div>
        </div>

        <div class="button-wrapper">
            <button class="button get-results-button" :disabled="loading" @click="updateOffers">
                {{ $t('results.heroBanner.buttons.updateResults') }}
                <div class="spinner-border spinner-border-sm text-light ms-1" v-show="loading">
                    <span class="sr-only">{{ $t('form.general.loading') }}...</span>
                </div>
            </button>
        </div>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { useForm } from 'vee-validate'
import { updateOffers } from '@js/functions/resubmit.js'
import { ref } from 'vue'

export default {
    props: {
        showResubmit: Boolean,
        maxLoanAmount: [String, Number],
        minLoanAmount: [String, Number],
        loanTerms: Array,
        loanValues: Object,
    },
    setup() {
        const store = useStore()
        const { validate } = useForm()
        const loanAmountComponent = ref(null)
        const loanTermComponent = ref(null)

        return {
            validate,
            loanTermComponent,
            loanAmountComponent,
            store,
        }
    },
    data() {
        return {
            loading: false,
            errors: [],
            translations: {
                loanAmount: {
                    label: this.$t('results.resubmit.fields.loanAmount.label'),
                    errorMessage: this.$t('results.resubmit.fields.loanAmount.errorMessage'),
                },
                loanTerm: {
                    label: this.$t('results.resubmit.fields.loanTerm.label'),
                    errorMessage: this.$t('results.resubmit.fields.loanTerm.errorMessage'),
                },
            },
        }
    },
    mounted() {
        this.emitter.on('reSubmitted', this.reSubmitted)
    },
    computed: {
        loanAmountInputLabel: function () {
            return window.innerWidth > 992 ? 'Loan Amount' : 'Amount'
        },
        loanAmount: function () {
            return this.store.getters.fieldValue('loanAmount', 'data', true)
        },
        loanTerm: function () {
            return this.store.getters.fieldValue('loanTerm', 'data', true)
        },
        loanPurpose: function () {
            return this.store.getters.fieldValue('loanPurpose', 'data', true)
        },
        status: function () {
            return this.$store.state.result.status
        },
        territory: function () {
            return process.env.TERRITORY
        },
    },
    methods: {
        async updateOffers() {
            const validation = await this.validate()
            if (validation.valid) {
                this.loading = true

                this.trackEvent('event', {
                    eventCategory: 'form',
                    eventAction: 'submit',
                    eventLabel: 'resubmit-form',
                    eventValue: this.loanAmount,
                })
                updateOffers(this.$store, this)
            }
        },
        reSubmitted: function () {
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';
@import '~scss/mixins/rem';

.col {
    padding-right: 0px;
    padding-left: 0px;

    &.resubmit-container {
        display: flex;
        flex-direction: column;

        @media screen and (min-width: $bp-large) {
            flex-direction: row;
        }

        .inputs-wrapper {
            width: 100%;
            display: flex;
            flex-direction: column;

            @media screen and (min-width: $bp-large) {
                flex-direction: row;
                width: 75%;
                &.territory-usa {
                    width: 36.5% !important;
                }
            }

            .input-container {
                width: 100%;
                border-bottom: 1px solid #ebeef4;
                padding-bottom: 30px;
                padding-top: 30px;

                &:first-child {
                    padding-top: 0px;
                }

                @media screen and (min-width: $bp-large) {
                    border: none;
                    padding-bottom: 0px;
                    padding-top: 0px;
                    margin-right: 10px;

                    &:first-child {
                        padding-top: 0px;
                    }

                    &:last-child {
                        margin-right: 0px;
                    }
                }

                .label {
                    color: var(--text-dark);
                    font-size: 20px;
                    font-weight: 700;
                    line-height: 24px;
                    letter-spacing: -0.01em;
                    text-align: left;
                    margin-bottom: 20px;

                    @media screen and (min-width: $bp-large) {
                        display: none;
                    }
                }

                .desktop-only {
                    @media screen and (max-width: $bp-large) {
                        display: none;
                    }
                }

                .mobile-only {
                    @media screen and (min-width: $bp-large) {
                        display: none;
                    }
                }
            }
        }

        .button-wrapper {
            width: 100%;
            padding-bottom: 30px;
            padding-top: 30px;

            button {
                line-height: rem(53px);
                max-width: 100%;
            }

            @media screen and (min-width: $bp-large) {
                flex-direction: row;
                width: 30%;
                min-width: 144px;
                margin-left: 10px;
                padding-bottom: 0px;
                padding-top: 0px;
            }
        }
    }
}
</style>
